import React from 'react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

function getInitialColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6'];
  return colors[Math.abs(hash) % colors.length];
}

const ParticipantPanelWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';

  if (!state) return null;

  const { facilitators = [], sessionAvatars } = state;

  return (
    <div className="flex flex-col h-full overflow-auto px-4 py-4 gap-5">
      {/* Facilitators */}
      {facilitators.length > 0 && (
        <div>
          <div className={`text-sm font-semibold uppercase tracking-wider mb-3 ${
            isDark ? 'text-white/30' : 'text-slate-400'
          }`}>
            Facilitators
          </div>
          <div className="flex flex-col gap-3">
            {facilitators.map((facilitator) => (
              <div key={facilitator.id} className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
                  style={{ backgroundColor: getInitialColor(facilitator.name) }}
                >
                  {facilitator.name.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className={`text-base font-semibold truncate ${
                    isDark ? 'text-white/80' : 'text-slate-700'
                  }`}>
                    {facilitator.name}
                  </div>
                  <div className={`text-sm truncate ${
                    isDark ? 'text-white/30' : 'text-slate-400'
                  }`}>
                    {facilitator.role}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Divider */}
      {facilitators.length > 0 && sessionAvatars.length > 0 && (
        <div className={`border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`} />
      )}

      {/* AI Avatars */}
      {sessionAvatars.length > 0 && (
        <div>
          <div className={`text-sm font-semibold uppercase tracking-wider mb-3 ${
            isDark ? 'text-white/30' : 'text-slate-400'
          }`}>
            AI Avatars
          </div>
          <div className="flex flex-col gap-3">
            {sessionAvatars.map((avatar) => (
              <div key={avatar.role} className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center text-base font-bold shrink-0"
                  style={{ backgroundColor: avatar.primaryColor || '#475569' }}
                >
                  {avatar.imageUrl ? (
                    <img src={avatar.imageUrl} alt={avatar.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white">{avatar.emoji || avatar.name.charAt(0)}</span>
                  )}
                </div>
                <div className="min-w-0">
                  <div className={`text-base font-semibold truncate ${
                    isDark ? 'text-white/80' : 'text-slate-700'
                  }`}>
                    {avatar.name}
                  </div>
                  <div className={`text-sm truncate ${
                    isDark ? 'text-white/30' : 'text-slate-400'
                  }`}>
                    {avatar.role}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!facilitators.length && !sessionAvatars.length && (
        <div className={`flex items-center justify-center h-full text-sm ${
          isDark ? 'text-white/30' : 'text-slate-400'
        }`}>
          No participants
        </div>
      )}
    </div>
  );
};

export default ParticipantPanelWidget;
